/** Pure helpers for the unified "生成创意" (`generate_creative`) node
 * (VISTOERA_CANVAS_PROJECT_START_TO_GENERATE_CREATIVE_UNIFIED_GENERATION_
 * NODE_V2). Sibling of `creativeIdeationCanvas.ts` (kept fully intact and
 * unmodified, for the LEGACY `project_start`/`creative_candidate`
 * rendering/dispatch path) -- this file is purely ADDITIVE, never a
 * rewrite of it.
 *
 * V2 spec section 1 "generation node = generator + current result
 * carrier" collapses V1's two-node model (`project_start` Brief SOURCE
 * plus `creative_candidate` RESULT x3, each its own Canvas node) into
 * ONE node that owns both a Brief (this file reuses
 * `creativeIdeationCanvas.ts`'s `buildBriefPayload` / `briefFingerprint`
 * / `isBriefAffectingPatch` verbatim -- the brief field names are
 * unchanged, so those helpers work unmodified against a
 * `generate_creative` node's `fields`) and its OWN current result
 * (`fields.currentResult`, nested so it never collides with the
 * brief's own `tone` field -- a Brief has a `tone` "style hint" field,
 * a generated candidate has a DIFFERENT `tone` "voice/mood" attribute;
 * nesting the result keeps both namespaces separate without renaming
 * either legacy field). */

import type { Node } from '@xyflow/react'
import type { CreativeIdeationCandidate } from '../../../api'
import type { FlowNodeData } from './types'

/** One materialized AI candidate, exactly as stored under a
 * `generate_creative` node's own `fields.currentResult` -- same shape
 * `buildParentCandidatePayload` (creativeIdeationCanvas.ts) already
 * knows how to read for a Diverge request's `parent_candidate`, so a
 * node's `currentResult` can be passed to that helper unchanged. */
export interface GenerateCreativeResult {
  candidateId: string
  title: string
  premise: string
  hook: string
  tone: string
  storyDirection: string
  productRole: string | null
  characterRole: string | null
  estimatedDuration: number | null
  tags: string[]
  rationale: string
  sourceRunId: string
  stale: boolean
}

export function buildResultFromCandidate(candidate: CreativeIdeationCandidate, sourceRunId: string): GenerateCreativeResult {
  return {
    candidateId: candidate.candidate_id,
    title: candidate.title,
    premise: candidate.premise,
    hook: candidate.hook,
    tone: candidate.tone,
    storyDirection: candidate.story_direction,
    productRole: candidate.product_role ?? null,
    characterRole: candidate.character_role ?? null,
    estimatedDuration: candidate.estimated_duration ?? null,
    tags: Array.isArray(candidate.tags) ? candidate.tags : [],
    rationale: candidate.rationale ?? '',
    sourceRunId,
    stale: false,
  }
}

/** Brief-only fields carried from a parent `generate_creative` node into
 * each of its 3 Diverge children (V2 spec section 18B -- children are
 * "ordinary, complete, independent generate_creative nodes" that
 * inherit the SAME context they diverged from, then are fully
 * independently editable). Deliberately excludes run bookkeeping
 * (activeRunId/runStatus/...) and `currentResult` itself (each child
 * gets its OWN candidate, set separately below). */
const INHERITABLE_BRIEF_KEYS = [
  'userIntent',
  'contentGoal',
  'contentGoalSource',
  'targetDurationSeconds',
  'durationSource',
  'assets',
  'mustInclude',
  'mustAvoid',
  'audience',
  'platform',
  'tone',
  'references',
  'skillSelectionSource',
  'skillKey',
  'providerSelectionMode',
  'providerOptionId',
] as const

export function buildDivergedChildFields(
  candidate: CreativeIdeationCandidate,
  parentFields: Record<string, unknown>,
  ctx: { parentNodeId: string; rootRunId: string; generationDepth: number; sourceRunId: string },
): Record<string, unknown> {
  const inherited: Record<string, unknown> = {}
  for (const key of INHERITABLE_BRIEF_KEYS) {
    if (key in parentFields) inherited[key] = parentFields[key]
  }
  return {
    ...inherited,
    currentResult: buildResultFromCandidate(candidate, ctx.sourceRunId),
    parentNodeId: ctx.parentNodeId,
    rootRunId: ctx.rootRunId,
    generationDepth: ctx.generationDepth,
    activeRunId: null,
    runStatus: null,
    runErrorMessage: null,
    activeDivergeRunId: null,
    divergeRunStatus: null,
    divergeRunErrorMessage: null,
    moreRequirementsExpanded: false,
  }
}

/** VISTOERA_GENERATE_CREATIVE_CANONICAL_GENERATION_CARD_UI_DIVERGE_
 * TRIPLET_AND_SINGLE_EXPANSION_V3 §39/§40 Duplicate -- "copy input
 * parameters, Prompt, Structured Context Assets, Provider selection,
 * Skill selection...forming an independent new node" -- deliberately
 * does NOT copy `currentResult`/any run bookkeeping/lineage (a
 * duplicate is a fresh, independent draft, never a clone of an
 * in-flight or completed generation -- same "never copy execution"
 * rule `canvasGenerationExecutionStrip.ts`'s `resetGenerationStatusToDraft`
 * already enforces for media generation nodes). */
export function buildGenerateCreativeDuplicateFields(originFields: Record<string, unknown>): Record<string, unknown> {
  const inherited: Record<string, unknown> = {}
  for (const key of INHERITABLE_BRIEF_KEYS) {
    if (key in originFields) inherited[key] = originFields[key]
  }
  return {
    ...inherited,
    moreRequirementsExpanded: false,
    currentResult: null,
    activeRunId: null,
    runStatus: null,
    runErrorMessage: null,
    activeDivergeRunId: null,
    divergeRunStatus: null,
    divergeRunErrorMessage: null,
    // A duplicate is a brand-new root -- never inherits the origin's
    // own lineage/divergence-group membership (spec §40 "形成独立
    // 节点"; it is not a fourth sibling of whatever group the origin
    // belonged to).
    parentNodeId: null,
    rootRunId: null,
    generationDepth: 0,
    divergenceGroupId: null,
    divergenceSiblingIndex: null,
    divergenceOriginNodeId: null,
  }
}

/** VISTOERA_GENERATE_CREATIVE_CANONICAL_GENERATION_CARD_UI_DIVERGE_
 * TRIPLET_AND_SINGLE_EXPANSION_V3 §18-§27 -- Diverge's NEW, corrected
 * materialization contract: exactly 3 total result nodes where the
 * CURRENT node is reused as one of them (§20 "Diverge 对当前节点相当于
 * Regenerate current branch + create two sibling branches"), never "+3
 * new nodes on top of the untouched current one" (the OLD, now-wrong
 * V2 behavior `buildDivergedChildFields` above implemented).
 *
 * Non-visible provenance only (§48/§49 "VISIBLE_PARENT_CHILD_EDGE=NO")
 * -- `divergenceGroupId`/`divergenceSiblingIndex`/`divergenceOriginNodeId`
 * tie the 3 siblings together for lineage/debugging without ever
 * becoming a Canvas edge. */
export interface DivergenceSiblingContext {
  divergenceGroupId: string
  siblingIndex: 1 | 2
  originNodeId: string
  rootRunId: string
  generationDepth: number
  runId: string
}

/** Fields for a BRAND-NEW sibling node (B/C) materialized the instant
 * the `/expand` dispatch succeeds (§23 "点击确认后立即 materialize
 * A/B/C") -- created with `runStatus: 'GENERATING'` and NO result yet
 * (reuses the exact same EMPTY/GENERATING/SUCCESS/ERROR result-area
 * states a plain Generate already renders, §45 "未来 Generation Card
 * Shell 应该复用这三/五个固定状态", no new UI state invented). Inherits
 * the SAME Brief/Assets/Provider/Skill the origin node had at the
 * moment Diverge was clicked (§18B's own "children inherit the context
 * they diverged from, then are fully independently editable" carried
 * over unchanged from V2). */
export function buildDivergenceSiblingFields(
  originFields: Record<string, unknown>,
  ctx: DivergenceSiblingContext,
): Record<string, unknown> {
  const inherited: Record<string, unknown> = {}
  for (const key of INHERITABLE_BRIEF_KEYS) {
    if (key in originFields) inherited[key] = originFields[key]
  }
  return {
    ...inherited,
    moreRequirementsExpanded: false,
    currentResult: null,
    activeRunId: ctx.runId,
    runStatus: 'GENERATING',
    runErrorMessage: null,
    activeDivergeRunId: null,
    divergeRunStatus: null,
    divergeRunErrorMessage: null,
    parentNodeId: ctx.originNodeId,
    rootRunId: ctx.rootRunId,
    generationDepth: ctx.generationDepth,
    divergenceGroupId: ctx.divergenceGroupId,
    divergenceSiblingIndex: ctx.siblingIndex,
    divergenceOriginNodeId: ctx.originNodeId,
  }
}

/** Applies ONE divergence candidate onto an EXISTING node's own
 * `currentResult` (used for all 3 members of a divergence group once
 * the single AI run reaches SUCCESS -- the origin node A included, per
 * §20's "Diverge = Regenerate current branch" for A itself). The node's
 * previous `currentResult`, if any, is simply overwritten -- it is not
 * lost: the OLD result is still fully recoverable via Generation
 * History, exactly like a plain Regenerate (§20 "旧结果...进入
 * Generation History, 不会丢失" -- `list_node_history` already maps
 * every past `CreativeIdeationRun` row for this `canvas_node_id`, and
 * this diverge run's own row is one of them; no separate history
 * write is needed here, see `creative_ideation_service.list_node_history`). */
export function applyDivergenceCandidateResult(
  candidate: CreativeIdeationCandidate,
  runId: string,
): { currentResult: GenerateCreativeResult; runStatus: null; runErrorMessage: null } {
  return {
    currentResult: buildResultFromCandidate(candidate, runId),
    runStatus: null,
    runErrorMessage: null,
  }
}

/** V2 spec section 12 Stale -- flips `fields.currentResult.stale = true`
 * on exactly ONE node (no descendant walk -- a `generate_creative`
 * node's Diverge children are fully independent nodes with their OWN
 * Brief copy, never cascade-marked by their parent's later edits,
 * unlike legacy V1's `markCandidatesStale`). No-op if there is no
 * result yet to go stale. */
export function markGenerateCreativeResultStale(nodes: Node[], nodeId: string): Node[] {
  return nodes.map((n) => {
    if (n.id !== nodeId) return n
    const data = n.data as unknown as FlowNodeData
    const result = data.fields?.currentResult as GenerateCreativeResult | null | undefined
    if (!result || result.stale) return n
    return {
      ...n,
      data: {
        ...data,
        fields: { ...data.fields, currentResult: { ...result, stale: true } },
      } as unknown as Record<string, unknown>,
    }
  })
}
